// src/utils/guestStoryStorage.ts
// This handles story storage for guest users before they register

export interface GuestStory {
    id: string;
    title: string;
    type: string;
    thumbnail?: string;
    content: {
      chapters: Array<{
        id: number;
        title: string;
        content: string;
      }>;
    };
    planData?: any;
    createdAt: string;
    updatedAt: string;
  }
  
  const GUEST_STORIES_KEY = 'scriblish_guest_drafts';
  
  // Get all guest stories
  export const getGuestStories = (): GuestStory[] => {
    try {
      const stored = localStorage.getItem(GUEST_STORIES_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading guest stories:', error);
      return [];
    }
  };
  
  // Get a single guest story by ID
  export const getGuestStory = (storyId: string): GuestStory | null => {
    const stories = getGuestStories();
    return stories.find(s => s.id === storyId) || null;
  };
  
  // Save a guest story (create or update)
  export const saveGuestStory = (story: GuestStory): void => {
    try {
      const stories = getGuestStories();
      const existingIndex = stories.findIndex(s => s.id === story.id);
      
      story.updatedAt = new Date().toISOString();
      
      if (existingIndex >= 0) {
        // Update existing
        stories[existingIndex] = story;
      } else {
        // Add new
        stories.push(story);
      }
      
      localStorage.setItem(GUEST_STORIES_KEY, JSON.stringify(stories));
      console.log('✅ Guest story saved:', story.id);
    } catch (error) {
      console.error('Error saving guest story:', error);
    }
  };
  
  // Delete a guest story
  export const deleteGuestStory = (storyId: string): void => {
    try {
      const stories = getGuestStories();
      const filtered = stories.filter(s => s.id !== storyId);
      localStorage.setItem(GUEST_STORIES_KEY, JSON.stringify(filtered));
      console.log('✅ Guest story deleted:', storyId);
    } catch (error) {
      console.error('Error deleting guest story:', error);
    }
  };
  
  // Clear all guest stories (after migration)
  export const clearGuestStories = (): void => {
    localStorage.removeItem(GUEST_STORIES_KEY);
    console.log('✅ Guest stories cleared');
  };
  
  // Check if user has any guest stories
  export const hasGuestStories = (): boolean => {
    return getGuestStories().length > 0;
  };
  
  // Generate a unique story ID for guests
  export const generateGuestStoryId = (): string => {
    return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };
  